import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
} from "recharts";
import propertyPerformanceData from "./__mock__/propertyPerformance.json";
import SubContainer from "./SubContainer";
import OptionContainer from "./OptionContainer";
import OptionButton from "./OptionButton";
import usePropertyWisePerformanceData from "./hooks/usePropertyWisePerformanceData";
import { useSelector } from "react-redux";
import { getCurrencySymbol } from "../../../Utils/Utils";
const metricsList = ["Revenue", "Occupancy", "ADR", "RevPAR"];
const periodList = [
  "Today",
  "MTD",
  "YTD",
  //  "Last Year MTD", "Last Year YTD"
];

const CustomTooltip = ({
  active,
  payload,
  label,
  currency = "INR",
  metric,
  currencySymbol,
  doComparison,
}) => {
  if (!active || !payload?.length) return null;

  const currentYear = new Date().getFullYear();
  const isCurrencyMetric = ["Revenue", "ADR", "RevPAR"].includes(metric);

  const formatValue = (value) => {
    if (isCurrencyMetric) {
      return `${currencySymbol}${value.toLocaleString()}`;
    }
    return metric === "Occupancy" ? `${value}%` : value;
  };

  const getLabel = (entryName) => {
    if (doComparison) {
      return entryName.startsWith("ly_") ? currentYear - 1 : currentYear;
    }
    return metric;
  };
  return (
    <Box
      sx={{
        backgroundColor: "#1F2937",
        border: "1px solid #4B5563",
        borderRadius: "8px",
        padding: "0.75rem 1rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        color: "#E5E7EB",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          marginBottom: "0.5rem",
          color: "#F3F4F6",
        }}
      >
        {label}
      </Typography>

      <Stack spacing={0.5}>
        {payload.map((entry, index) => (
          <Stack
            key={`item-${index}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Colored indicator dot */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: entry.color || "#60A5FA",
                }}
              />
              <Typography variant="body2" sx={{ color: "#D1D5DB" }}>
                {getLabel(entry.name)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
              {formatValue(entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const PropertyWisePerformance = ({
  data,
  isLoading,
  doComparison,
  comparisonData,
}) => {
  const hotels = useSelector((state) => state.auth.hotels);
  const hotelCurrency = hotels[0]?.hotelCurrency || "INR";
  const currencySymbol = getCurrencySymbol(hotelCurrency);

  const {
    chartData,
    comparisonChartData,
    mergedChartData,
    period,
    metric,
    handlePeriodChange,
    handleMetricChange,
    dataKey,
  } = usePropertyWisePerformanceData(data, comparisonData);

  const calculateChartWidth = (size, hasComparison = false) => {
    if (size > 6) {
      return size * 150 * (hasComparison ? 2 : 1);
    }
    return "100%";
  };
  if (isLoading) {
    return (
      <Box height={"320px"}>
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={320}
          animation="wave"
          sx={{
            borderRadius: "8px",
            "&.MuiSkeleton-root": {
              backgroundColor: "#b6bed350",
            },
          }}
        />
      </Box>
    );
  }

  return (
    <SubContainer spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <Typography variant="h5" fontWeight={700} color={"white"}>
            Property-wise Performance
          </Typography>
          <OptionContainer>
            {periodList.map((p) => (
              <OptionButton
                text={p}
                selected={period === p}
                onClick={() => handlePeriodChange(p)}
              />
            ))}
          </OptionContainer>
        </Stack>

        <OptionContainer>
          {metricsList.map((m) => (
            <OptionButton
              onClick={() => handleMetricChange(m)}
              selected={metric === m}
              text={m}
            />
          ))}
        </OptionContainer>
      </Stack>

      <div style={{ minWidth: "100%", overflowX: "auto" }}>
        <ResponsiveContainer
          width={calculateChartWidth(chartData.length, doComparison)}
          height={320}
        >
          <ComposedChart
            data={mergedChartData}
            width={"100%"}
            margin={{ left: 16, top: 16 }}
          >
            <XAxis
              tickFormatter={(label) =>
                label.length > 15 ? label.slice(0, 15) + "..." : label
              }
              dataKey="hotelName"
              tick={{ fill: "#b6bed3" }}
            />
            <YAxis
              tickFormatter={(v) => {
                const isCurrencyMetric = ["revenue", "adr", "revpar"].includes(
                  metric.toLowerCase()
                );
                if (isCurrencyMetric) {
                  return `${currencySymbol}${v.toLocaleString()}`;
                }
                return metric.toLowerCase() === "occupancy" ? `${v}%` : v;
              }}
              tick={{ fill: "#b6bed3" }}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  metric={metric}
                  currencySymbol={currencySymbol}
                  doComparison={doComparison}
                />
              )}
              cursor={{ fill: "transparent" }}
            />
            {doComparison && (
              <Bar
                dataKey={"ly_" + dataKey}
                fill="#4A5568"
                barSize={100}
                radius={[12, 12, 0, 0]}
                stroke="#718096"
              />
            )}
            <Bar
              dataKey={dataKey}
              fill="#4A5568"
              barSize={100}
              radius={[12, 12, 0, 0]}
              stroke="#718096"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </SubContainer>
  );
};

export default PropertyWisePerformance;
